"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Stack } from "@/components/ui/elements";
import { InputField, TextAreaField } from "@/components/ui/parts";
import { i18n } from "@/i18n";
import { makeClient } from "@/libs/apollo";
import { phoneNumberValidation } from "@/types/validation";
import { showNotification } from "@/util/userNotifications";

import { AddReactivationRequestMutation } from "./_graphql";

const schema = z.object({
  phoneNumber: phoneNumberValidation(),
  message: z.string().min(1, "申請理由を入力してください"),
});

type Schema = z.infer<typeof schema>;

type ReactivationRequestFormProps = {
  onSuccess?: (status: string) => void;
  onError?: () => void;
  phoneNumber: string;
};

export function ReactivationRequestForm({
  onSuccess,
  onError,
  phoneNumber,
}: ReactivationRequestFormProps) {
  const client = makeClient();
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit } = useForm<Schema>({
    mode: "onSubmit",
    defaultValues: {
      phoneNumber: phoneNumber,
      message: "",
    },
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: Schema) => {
    setLoading(true);
    try {
      const { data: result } = await client.mutate({
        mutation: AddReactivationRequestMutation,
        variables: {
          input: {
            message: data.message ?? "",
            phoneNumber: data.phoneNumber ?? "",
          },
        },
      });

      const status = result?.addReactivationRequest?.loginRestriction?.status;
      if (status) {
        showNotification({
          type: "info",
          message:
            "制限解除申請を送信しました。審査には数日〜1週間程度お時間をいただく場合がございます。",
        });
        onSuccess?.(status);
      }
    } catch (error) {
      const err = error as Error;
      showNotification({
        type: "error",
        message: err.message || "送信に失敗しました。",
      });
      onError?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="md">
        <InputField
          control={control}
          name="phoneNumber"
          label={i18n("phoneNumber")}
          placeholder="例：08010002000"
          withAsterisk
        />
        <TextAreaField
          control={control}
          name="message"
          label="申請理由"
          placeholder="解除の理由やお問い合わせ内容をご記入ください"
          minRows={4}
          radius="md"
        />
        <Button
          type="submit"
          loading={loading}
          color="blue"
          fullWidth
          size="md"
        >
          制限解除申請を送信
        </Button>
      </Stack>
    </form>
  );
}
